import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { editorCardProvider } from '@atlaskit/editor-card-provider';
import type { MentionNodeDataProvider } from '@atlaskit/editor-common/mention';
import { syncBlockMessages } from '@atlaskit/editor-common/messages';
import type { ProfilecardProvider } from '@atlaskit/editor-common/provider-factory';
import { SYNCED_BLOCKS_DOCUMENTATION_URL } from '@atlaskit/editor-common/sync-block';
import { type PublicPluginAPI } from '@atlaskit/editor-common/types';
import { createDefaultPreset } from '@atlaskit/editor-core/preset-default';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics/analyticsPluginType';
import type {
	AutocompletePlugin,
	AutocompletePluginOptions,
} from '@atlaskit/editor-plugin-autocomplete';
import { autocompletePlugin } from '@atlaskit/editor-plugin-autocomplete/autocompletePlugin';
import { cardPlugin, type CardPlugin } from '@atlaskit/editor-plugin-card';
import { extensionPlugin, type ExtensionPlugin } from '@atlaskit/editor-plugin-extension';
import { type FocusPlugin } from '@atlaskit/editor-plugin-focus';
import { gridPlugin, type GridPlugin } from '@atlaskit/editor-plugin-grid';
import { type HyperlinkPlugin } from '@atlaskit/editor-plugin-hyperlink';
import { listPlugin, type ListPlugin } from '@atlaskit/editor-plugin-list';
import { type MaxContentSizePlugin } from '@atlaskit/editor-plugin-max-content-size';
import { mentionsPlugin, type MentionsPlugin } from '@atlaskit/editor-plugin-mentions';
import type { MentionsChangedHandler } from '@atlaskit/editor-plugin-mentions/types';
import { type PlaceholderPlugin } from '@atlaskit/editor-plugin-placeholder';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';
import { saveOnEnterPlugin, type SaveOnEnterPlugin } from '@atlaskit/editor-plugin-save-on-enter';
import { textColorPlugin, type TextColorPlugin } from '@atlaskit/editor-plugin-text-color';
import { type TypeAheadPlugin } from '@atlaskit/editor-plugin-type-ahead';
import { type EditorView } from '@atlaskit/editor-prosemirror/view';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import type { LinkPickerProps, LinkPickerUiOptions } from '@atlaskit/link-picker/types';
import type { CardContext } from '@atlaskit/link-provider/types';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';
import { useLinkPickerEditorProps } from '@atlassian/link-picker-plugins/editor';
import {
	ROVO_LINK_PICKER_HEIGHT_IN_PX,
	ROVO_LINK_PICKER_WIDTH_IN_PX,
} from '@atlassian/rovo-link-picker/constants';
import type { Providers as RovoLinkPickerProviders } from '@atlassian/rovo-link-picker/provider-types';

import { arrowKeyPlugin, useArrowkeyPlugin, type UserMessage } from './arrow-key-plugin';
import {
	connectorSourceInputRulePlugin,
	type ConnectorSourceEntry,
} from './connector-source-input-rule';
import { connectorSourceHydrationKey } from './connector-source-input-rule/connector-source-hydration-key';
import {
	creationInlineSuggestionPlugin,
	type InlineSuggestionConfig,
} from './creation-inline-suggestion-plugin';
import { historyCompletionPlugin, type HistoryCompletionOptions } from './historyCompletionPlugin';
import messages from './messages';
import { pasteTextColorPlugin } from './paste-text-color-plugin';

const RovoLinkPicker = lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/rovo-link-picker" */
		'@atlassian/rovo-link-picker/RovoLinkPicker'
	).then((module) => ({ default: module.default })),
);

const styles = cssMap({
	rovoLinkPickerScrollContainer: {
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.small'),
		boxShadow: token('elevation.shadow.overlay'),
		overflowX: 'hidden',
		overflowY: 'auto',
	},
});

const ROVO_LINK_PICKER_VIEWPORT_MARGIN_IN_PX = 24;

const createRovoLinkPickerRootComponent = (
	cloudId: string,
	width: number,
	maxHeight?: number,
	maxHeightResolver?: () => number,
	horizontalOffset: number = 0,
	providers?: Partial<RovoLinkPickerProviders>,
	hideMetadataForProducts?: readonly string[],
	horizontalOffsetAnchorResolver?: () => HTMLElement | null,
	shouldCloseOnOutsideClick: boolean = false,
): NonNullable<LinkPickerProps['component']> => {
	const RovoLinkPickerRootComponent: NonNullable<LinkPickerProps['component']> = ({
		children: _children,
		displayText,
		hideDisplayText,
		inputRef,
		isSubmitting,
		onCancel,
		onSubmit,
		url,
	}) => {
		const resolveMaxHeight = useCallback(() => maxHeightResolver?.() ?? maxHeight, []);
		const [resolvedMaxHeight, setResolvedMaxHeight] = useState(resolveMaxHeight);
		const scrollContainerRef = useRef<HTMLDivElement | null>(null);
		const [resolvedHorizontalOffset, setResolvedHorizontalOffset] = useState(0);
		const resolvedHorizontalOffsetRef = useRef(resolvedHorizontalOffset);
		const horizontalOffsetAnimationFrameRef = useRef<number | null>(null);
		const onCancelRef = useRef(onCancel);
		resolvedHorizontalOffsetRef.current = resolvedHorizontalOffset;
		onCancelRef.current = onCancel;
		const updateHorizontalOffset = useCallback(() => {
			const scrollContainer = scrollContainerRef.current;
			if (!horizontalOffset && !horizontalOffsetAnchorResolver) {
				return;
			}
			if (!scrollContainer?.isConnected) {
				setResolvedHorizontalOffset(0);
				return;
			}

			const scrollContainerRect = scrollContainer.getBoundingClientRect();
			const unshiftedLeft = scrollContainerRect.left - resolvedHorizontalOffsetRef.current;
			const anchorLeft = horizontalOffsetAnchorResolver?.()?.getBoundingClientRect().left;
			const preferredHorizontalOffset =
				anchorLeft === undefined ? horizontalOffset : anchorLeft + horizontalOffset - unshiftedLeft;
			const viewportLeft = window.visualViewport?.offsetLeft ?? 0;
			const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
			const viewportRight = viewportLeft + viewportWidth;
			const unshiftedRight = scrollContainerRect.right - resolvedHorizontalOffsetRef.current;
			const minimumHorizontalOffset = Math.ceil(
				viewportLeft + ROVO_LINK_PICKER_VIEWPORT_MARGIN_IN_PX - unshiftedLeft,
			);
			const maximumHorizontalOffset = Math.floor(
				viewportRight - ROVO_LINK_PICKER_VIEWPORT_MARGIN_IN_PX - unshiftedRight,
			);
			const nextHorizontalOffset = Math.min(
				maximumHorizontalOffset,
				Math.max(minimumHorizontalOffset, preferredHorizontalOffset),
			);

			setResolvedHorizontalOffset((currentHorizontalOffset) =>
				currentHorizontalOffset === nextHorizontalOffset
					? currentHorizontalOffset
					: nextHorizontalOffset,
			);
		}, []);
		const setScrollContainer = useCallback(
			(scrollContainer: HTMLDivElement | null) => {
				scrollContainerRef.current = scrollContainer;
				if (horizontalOffsetAnimationFrameRef.current !== null) {
					cancelAnimationFrame(horizontalOffsetAnimationFrameRef.current);
					horizontalOffsetAnimationFrameRef.current = null;
				}
				if (scrollContainer) {
					updateHorizontalOffset();
					// The editor positions a reopened popup after its child ref is attached. Recalculate
					// once that placement has settled so a previously stored offset cannot move the
					// picker outside the viewport after the Rovo side panel is resized.
					horizontalOffsetAnimationFrameRef.current = requestAnimationFrame(() => {
						horizontalOffsetAnimationFrameRef.current = null;
						updateHorizontalOffset();
					});
				}
			},
			[updateHorizontalOffset],
		);

		useEffect(
			() => () => {
				if (horizontalOffsetAnimationFrameRef.current !== null) {
					cancelAnimationFrame(horizontalOffsetAnimationFrameRef.current);
				}
			},
			[],
		);

		useEffect(() => {
			if (
				(!horizontalOffset && !horizontalOffsetAnchorResolver) ||
				typeof ResizeObserver === 'undefined'
			) {
				return;
			}

			const observerTarget = horizontalOffsetAnchorResolver?.();
			if (!observerTarget) {
				return;
			}

			const resizeObserver = new ResizeObserver(updateHorizontalOffset);
			resizeObserver.observe(observerTarget);

			return () => resizeObserver.disconnect();
		}, [updateHorizontalOffset]);

		useEffect(() => {
			if (!maxHeightResolver) {
				return;
			}

			const updateMaxHeight = () => setResolvedMaxHeight(resolveMaxHeight());
			window.addEventListener('resize', updateMaxHeight);
			window.visualViewport?.addEventListener('resize', updateMaxHeight);
			window.visualViewport?.addEventListener('scroll', updateMaxHeight);

			return () => {
				window.removeEventListener('resize', updateMaxHeight);
				window.visualViewport?.removeEventListener('resize', updateMaxHeight);
				window.visualViewport?.removeEventListener('scroll', updateMaxHeight);
			};
		}, [resolveMaxHeight]);

		useEffect(() => {
			if (!horizontalOffset && !horizontalOffsetAnchorResolver) {
				return;
			}

			updateHorizontalOffset();
			window.addEventListener('resize', updateHorizontalOffset);
			window.visualViewport?.addEventListener('resize', updateHorizontalOffset);
			window.visualViewport?.addEventListener('scroll', updateHorizontalOffset);

			return () => {
				window.removeEventListener('resize', updateHorizontalOffset);
				window.visualViewport?.removeEventListener('resize', updateHorizontalOffset);
				window.visualViewport?.removeEventListener('scroll', updateHorizontalOffset);
			};
		}, [updateHorizontalOffset]);

		useEffect(() => {
			if (!shouldCloseOnOutsideClick) {
				return;
			}

			const handleClickAway = (event: MouseEvent) => {
				const scrollContainer = scrollContainerRef.current;
				if (
					!scrollContainer?.isConnected ||
					!(event.target instanceof Node) ||
					scrollContainer.contains(event.target)
				) {
					return;
				}

				onCancelRef.current?.();
			};

			// Capture clicks that Confluence page surfaces may stop before they reach the
			// editor's bubble-phase click-away listener.
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			window.addEventListener('click', handleClickAway, true);
			return () => {
				// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
				window.removeEventListener('click', handleClickAway, true);
			};
		}, []);

		if (!onSubmit) {
			return null;
		}

		const linkPicker = (
			<RovoLinkPicker
				cloudId={cloudId}
				displayText={displayText}
				hideDisplayText={hideDisplayText}
				hideMetadataForProducts={hideMetadataForProducts}
				inputRef={inputRef}
				isSubmitting={isSubmitting}
				onCancel={onCancel}
				onSubmit={onSubmit}
				providers={providers}
				url={url}
				width={width}
			/>
		);

		return (
			<Suspense fallback={null}>
				{resolvedMaxHeight ? (
					<Box
						ref={setScrollContainer}
						xcss={styles.rovoLinkPickerScrollContainer}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- The picker dimensions and viewport-safe offset are supplied by the host at runtime.
						style={{
							maxHeight: resolvedMaxHeight,
							transform: `translateX(${resolvedHorizontalOffset}px)`,
							width,
						}}
					>
						{linkPicker}
					</Box>
				) : (
					linkPicker
				)}
			</Suspense>
		);
	};

	return RovoLinkPickerRootComponent;
};

/**
 * The maximum number of characters that can be entered in the chat input.
 */
export const MAX_CHARACTERS_INPUT = 256000;

type EditorLinkPickerOptions = LinkPickerUiOptions &
	Pick<Partial<LinkPickerProps>, 'plugins'> & {
		/** Maximum height used by the Rovo picker and its editor popup placement. */
		rovoLinkPickerMaxHeight?: number;
		/** Resolves the maximum height when the picker opens or the viewport changes. */
		rovoLinkPickerMaxHeightResolver?: () => number;
		/** Preferred offset from the anchor's left edge, clamped inside the viewport. */
		rovoLinkPickerHorizontalOffset?: number;
		/** Resolves the element whose left edge anchors and whose resize refreshes the picker offset. */
		rovoLinkPickerHorizontalOffsetAnchorResolver?: () => HTMLElement | null;
		/** Closes the Rovo picker when a click lands outside its rendered surface. */
		rovoLinkPickerShouldCloseOnOutsideClick?: boolean;
		/** Optional data providers used by the Rovo picker. */
		rovoLinkPickerProviders?: Partial<RovoLinkPickerProviders>;
		/** Product keys whose Rovo picker results should omit the metadata line. */
		rovoLinkPickerHideMetadataForProducts?: readonly string[];
		/** Width used by the Rovo picker and its editor popup container. */
		rovoLinkPickerWidth?: number;
		shouldMergeAdditionalPlugins?: boolean;
	};

export type EditorPresetProps = {
	onSave: (view: EditorView) => void;
	productKey: string;
	siteId: string;
	arrowKeyPluginConfig: {
		userMessages: UserMessage[];
	};
	placeholderPrompts?: string[];
	placeholderPromptAnimationOptions?: {
		eraseDelay?: number;
		pauseBeforeErase?: number;
	};
	placeholderText?: string;
	inlineSuggestion?: InlineSuggestionConfig;
	/** Lazy history behavior; the editor adapter remains inert until this is supplied. */
	historyCompletion?: HistoryCompletionOptions;
	/** Optional override for Link Picker options */
	linkPickerOptions?: EditorLinkPickerOptions;
	/** Use the Rovo Search-powered link picker instead of the legacy plugin-based picker. */
	enableRovoLinkPicker?: boolean;
	/**
	 * Typically used when the container is small (e.g., chromeless editor with small width).
	 */
	useShortMessages?: boolean;
	connectorSources?: ConnectorSourceEntry[];
	autocomplete?: AutocompletePluginOptions;
	/**
	 * Ignored. The autocomplete plugin is registered synchronously in the
	 * initial preset so hosts that still pass `loadLocalModelAutocomplete`
	 * keep compiling. web-llm stays behind the plugin's own dynamic import
	 * when `useLocalModel` is true.
	 */
	loadAutocompletePlugin?: () => ReturnType<
		typeof import('./loadAutocompletePlugin').loadAutocompletePlugin
	>;
	/**
	 * When true, the @mention typeahead splits results into separate "People" and "Agents" sections.
	 */
	enableAgentSectioning?: boolean;
	/**
	 * Controls whether the @mention typeahead can open without changing the
	 * editor preset identity.
	 */
	canOpenMentionTypeAhead?: () => boolean;
	handleMentionsChanged?: MentionsChangedHandler;
	/** Optional Smart Card context scoped to this editor instance. */
	smartCardContext?: CardContext;
	mentionNodeDataProvider?: MentionNodeDataProvider;
	profilecardProvider?: Promise<ProfilecardProvider>;
};

export const useEditorPreset = ({
	onSave,
	arrowKeyPluginConfig,
	productKey,
	siteId,
	placeholderPrompts,
	placeholderPromptAnimationOptions,
	placeholderText,
	inlineSuggestion,
	historyCompletion,
	linkPickerOptions = {},
	enableRovoLinkPicker = false,
	useShortMessages = false,
	connectorSources = [],
	autocomplete,
	enableAgentSectioning = false,
	canOpenMentionTypeAhead,
	handleMentionsChanged,
	smartCardContext,
	mentionNodeDataProvider,
	profilecardProvider,
}: EditorPresetProps): {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	preset: any;
	/**
	 * This type needs to be defined explicitly otherwise we get deep import errors.
	 * This is a non-exhaustive list of plugins that are used in our preset - there are more default plugins but we want to avoid adding these to package.json if they are not used.
	 */
	editorApi?: PublicPluginAPI<
		[
			AnalyticsPlugin,
			CardPlugin,
			SaveOnEnterPlugin,
			MaxContentSizePlugin,
			MentionsPlugin,
			GridPlugin,
			QuickInsertPlugin,
			ListPlugin,
			TypeAheadPlugin,
			HyperlinkPlugin,
			FocusPlugin,
			PlaceholderPlugin,
			ExtensionPlugin,
			TextColorPlugin,
			AutocompletePlugin,
		]
	>;
} => {
	const { formatMessage } = useIntl();
	const {
		rovoLinkPickerHorizontalOffset = 0,
		rovoLinkPickerHorizontalOffsetAnchorResolver,
		rovoLinkPickerMaxHeight,
		rovoLinkPickerMaxHeightResolver,
		rovoLinkPickerProviders,
		rovoLinkPickerHideMetadataForProducts,
		rovoLinkPickerShouldCloseOnOutsideClick = false,
		rovoLinkPickerWidth = ROVO_LINK_PICKER_WIDTH_IN_PX,
		...legacyLinkPickerOptions
	} = linkPickerOptions;
	const resolveRovoLinkPickerHeight = () =>
		rovoLinkPickerMaxHeightResolver?.() ?? rovoLinkPickerMaxHeight;
	const legacyLinkPicker = useLinkPickerEditorProps(legacyLinkPickerOptions, {
		cloudId: siteId,
		product: productKey,
	});
	const rovoLinkPickerRootComponent = useMemo(
		() =>
			createRovoLinkPickerRootComponent(
				siteId,
				rovoLinkPickerWidth,
				rovoLinkPickerMaxHeight,
				rovoLinkPickerMaxHeightResolver,
				rovoLinkPickerHorizontalOffset,
				rovoLinkPickerProviders,
				rovoLinkPickerHideMetadataForProducts,
				rovoLinkPickerHorizontalOffsetAnchorResolver,
				rovoLinkPickerShouldCloseOnOutsideClick,
			),
		[
			siteId,
			rovoLinkPickerWidth,
			rovoLinkPickerMaxHeight,
			rovoLinkPickerMaxHeightResolver,
			rovoLinkPickerHorizontalOffset,
			rovoLinkPickerProviders,
			rovoLinkPickerHideMetadataForProducts,
			rovoLinkPickerHorizontalOffsetAnchorResolver,
			rovoLinkPickerShouldCloseOnOutsideClick,
		],
	);
	const linkPicker = enableRovoLinkPicker
		? {
				...legacyLinkPicker,
				component: rovoLinkPickerRootComponent,
				popupContainerSurface: 'none' as const,
				get popupHeight() {
					return resolveRovoLinkPickerHeight() ?? ROVO_LINK_PICKER_HEIGHT_IN_PX;
				},
				popupWidth: rovoLinkPickerWidth,
			}
		: legacyLinkPicker;

	const { handleArrowDown, handleArrowUp, handleInputChange } = useArrowkeyPlugin({
		userMessages: arrowKeyPluginConfig.userMessages,
	});

	const knownSourcesRef = useRef<ConnectorSourceEntry[]>(connectorSources);
	knownSourcesRef.current = connectorSources;
	const handleMentionsChangedRef = useRef(handleMentionsChanged);
	handleMentionsChangedRef.current = handleMentionsChanged;
	const smartCardContextRef = useRef(smartCardContext);
	smartCardContextRef.current = smartCardContext;
	const forwardMentionsChangedRef = useRef<MentionsChangedHandler>((changes) => {
		handleMentionsChangedRef.current?.(changes);
	});
	const shouldForwardMentionChanges = handleMentionsChanged !== undefined;
	const historyCompletionRef = useRef(historyCompletion);

	const autocompletePluginOptions = autocomplete
		? { ...autocomplete, surface: autocomplete.surface ?? 'rovo-chat' }
		: autocomplete;

	// productKey gate before the experiment read keeps exposure Confluence-only.
	// Register the plugin in the first preset so Editor Core never reconfigures
	// (and blur()s) after mount. web-llm stays in the plugin's dynamic import.
	const shouldEnableAutocomplete =
		!!autocompletePluginOptions &&
		productKey === 'confluence' &&
		isExperimentEnabled('platform_editor_ai_autocomplete_rovo_chat_editor');
	historyCompletionRef.current = shouldEnableAutocomplete ? undefined : historyCompletion;

	const { preset, editorApi } = usePreset(() => {
		const isUpdatedPlaceholderEnabled: () => boolean = () =>
			FeatureGates.getExperimentValue('rovo-chat-input-placeholder-updated', 'isEnabled', false);

		const placeholderConfig = placeholderPrompts
			? { placeholderPrompts, placeholderPromptAnimationOptions }
			: placeholderText
				? { placeholder: placeholderText }
				: {
						placeholder: formatMessage(
							useShortMessages
								? messages.placeholderShort
								: isUpdatedPlaceholderEnabled()
									? messages.placeholderUpdated
									: messages.placeholder,
						),
					};

		const presetBuilder = createDefaultPreset({
			allowAnalyticsGASV3: fg('platform_rovo_chat_enable_editor_plugin_analytics'),
			placeholder: placeholderConfig,
			paste: {
				pasteWarningOptions: {
					cannotPasteSyncedBlock: {
						title: syncBlockMessages.cannotPasteSyncedBlockTitle,
						description: syncBlockMessages.cannotPasteSyncedBlockUnsupportedDescription,
						urlHref: SYNCED_BLOCKS_DOCUMENTATION_URL,
						urlText: syncBlockMessages.cannotPasteSyncedBlockAction,
					},
				},
			},
			hyperlinkOptions: {
				linkPicker,
				lpLinkPicker: true,
			},
			blockType: {
				allowBlockType: {
					exclude: ['heading'],
				},
				lastNodeMustBeParagraph: false,
			},
			toolbar: {
				enableNewToolbarExperience: false,
			},
		})
			.add([saveOnEnterPlugin, onSave])
			.add([
				mentionsPlugin,
				{
					...(canOpenMentionTypeAhead ? { canOpenTypeAhead: canOpenMentionTypeAhead } : {}),
					enableAgentSectioning:
						enableAgentSectioning || isExperimentEnabled('rovo_chat_mention_agents'),
					handleMentionsChanged: shouldForwardMentionChanges
						? forwardMentionsChangedRef.current
						: undefined,
					...(mentionNodeDataProvider ? { mentionNodeDataProvider } : {}),
					...(profilecardProvider ? { profilecardProvider } : {}),
				},
			])
			.add(gridPlugin)
			.add([
				cardPlugin,
				{
					allowBlockCards: false,
					allowEmbeds: false,
					linkPicker,
					lpLinkPicker: true,
					provider: Promise.resolve(editorCardProvider),
					smartCardContext: smartCardContextRef.current,
				},
			])
			.add(listPlugin)
			.add(extensionPlugin)
			.add([connectorSourceInputRulePlugin, { knownSourcesRef }])
			.maybeAdd([creationInlineSuggestionPlugin, inlineSuggestion], !!inlineSuggestion)
			.add(textColorPlugin)
			.add(pasteTextColorPlugin)
			.maybeAdd([autocompletePlugin, autocompletePluginOptions], shouldEnableAutocomplete);

		// Register the plugin up front. Async history options must never change the
		// preset identity: Editor Core reconfiguration blurs the editor even for identical schemas.
		const presetWithHistory = presetBuilder.add([
			historyCompletionPlugin,
			{ getOptions: () => historyCompletionRef.current, onAccept: handleInputChange },
		]);

		return presetWithHistory.add([
			arrowKeyPlugin,
			{
				onArrowDown: handleArrowDown,
				onArrowUp: handleArrowUp,
				onInputChange: handleInputChange,
			},
		]);
	}, [inlineSuggestion, shouldForwardMentionChanges, mentionNodeDataProvider, profilecardProvider]);

	useEffect(() => {
		if (fg('platform_ai_mate_connector_injected_prompts') && connectorSources.length) {
			editorApi?.core.actions.execute(({ tr }) => tr.setMeta(connectorSourceHydrationKey, true));
		}
	}, [connectorSources, editorApi]);

	useEffect(() => {
		editorApi?.core.actions.execute(({ tr }) => tr.setMeta('historyCompletionRefresh', true));
	}, [
		editorApi,
		historyCompletion?.getCompletion,
		historyCompletion?.disabled,
		historyCompletion?.tracking,
		shouldEnableAutocomplete,
	]);

	return {
		preset,
		editorApi,
	};
};
